import { useEffect, useState } from 'react';
import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import './VideoPlayerPanel.css';

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6l5 5 5-5" />
    </svg>
  );
}

// A full-screen video panel that slides up from the bottom when a story with
// video is opened. The video fills the upper portion at its natural aspect
// ratio; the story headline and section sit below it. A backdrop tap or the
// chevron button centred just above the sheet dismisses the panel — both
// the sheet and the close button slide down together on exit.
export function VideoPlayerPanel() {
  const { current, stop } = useVideoPlayer();
  const [closing, setClosing] = useState(false);

  // When the context clears the current story (e.g. from elsewhere), unmount
  // without the exit animation — only animate when the user closes from here.
  useEffect(() => {
    if (!current) setClosing(false);
  }, [current]);

  if (!current && !closing) return null;

  const dismiss = () => {
    setClosing(true);
    // Match the sheet's slide-up duration so the exit completes before unmount.
    window.setTimeout(stop, 280);
  };

  return (
    <div
      className={
        closing
          ? 'video-panel video-panel--closing'
          : 'video-panel'
      }
      role="dialog"
      aria-modal="true"
      aria-label={current?.headline || 'Video player'}
    >
      <div className="video-panel__backdrop" onClick={dismiss} aria-hidden="true" />

      {!closing && (
        <button
          type="button"
          className="video-panel__close"
          onClick={dismiss}
          aria-label="Close video"
        >
          <ChevronDownIcon />
          <span>Close</span>
        </button>
      )}

      <div
        className={
          closing
            ? 'video-panel__sheet video-panel__sheet--closing'
            : 'video-panel__sheet'
        }
      >
        <div className="video-panel__handle" aria-hidden="true">
          <span className="video-panel__handle-bar" />
        </div>

        {current && (
          <>
            <div className="video-panel__video-wrap">
              <video
                className="video-panel__video"
                src={current.videoUrl}
                poster={current.image || undefined}
                controls
                autoPlay
                playsInline
                preload="metadata"
              />
            </div>

            <div className="video-panel__info">
              {current.section && (
                <span className="video-panel__eyebrow">{current.section}</span>
              )}
              <h2 className="video-panel__title">{current.headline}</h2>
              {current.author && (
                <p className="video-panel__byline">{current.author}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}